import React from "react";
import { useTranslation } from "react-i18next";
import { ConnectedProps, connect } from "react-redux";
import {
  $Row,
  $Col,
  $Divider,
  $Typography,
  $AmountLabel,
  $DateLabel,
  $Skeleton,
} from "us.common/components";
import { RootState } from "us.helper/types";
import { getRouteUrl } from "us.helper";
import { DateValues } from "../Constants";
import dayjs from "dayjs";

const { Paragraph } = $Typography;

/**
 * @description - Archive case and invoice detailed view.
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 30/10/2024
 * */
export const DataArchive: React.FC<PropsFromRedux> = (props) => {
  const { archivedDetails } = props;
  const {
    creditorNo,
    creditorName,
    balance,
    closeStateReason,
    archivedDate,
    archivedUser,
    closedDate,
  } = archivedDetails?.data ?? {};

  const { t } = useTranslation();

  return (
    <$Skeleton
      loading={archivedDetails?.isLoading}
      active
      paragraph={{ rows: 3 }}
    >
      <div className="archive-drawer-container">
        <$Row className="mb-3">
          <$Col span={16} className="p-2">
            <h5 className="text-muted">
              {t("US.COLLECTION.COMMON:COMMON.CREDITOR")}
            </h5>
            <strong>
              <Paragraph ellipsis={{ tooltip: true }} className="m-0">
                {!creditorNo && !creditorName && "-"}
                <a
                  onClick={() =>
                    getRouteUrl.moduleRoute(
                      "collection",
                      `/creditor/${creditorNo}`
                    )
                  }
                >
                  {creditorNo}
                </a>
                {creditorName && creditorNo && " | "} {creditorName}
              </Paragraph>
            </strong>
          </$Col>
          <$Col span={8} className="p-2 ab-bg-color">
            <h5 className="text-muted">
              {t("US.COLLECTION.COMMON:COMMON.CASE_BALANCE")}
            </h5>
            <strong>
              <$AmountLabel value={balance} />
            </strong>
          </$Col>
        </$Row>

        <$Row>
          <$Col span={16} className="p-2">
            <div className="w-100">
              <h5 className="text-muted">
                {t("US.COLLECTION.COMMON:COMMON.REASON")}
              </h5>
              <strong>
                <Paragraph ellipsis={{ tooltip: true }} className="m-0">
                  {!closeStateReason && "-"}
                  {closeStateReason}
                </Paragraph>
              </strong>
            </div>
          </$Col>
          <$Col span={8} className="ab-date-bg-color p-2">
            <h5 className="text-muted">
              {t("US.COLLECTION.COMMON:COMMON.CLOSED_DATE")}
            </h5>
            <strong>
              {!closedDate && "-"}
              {closedDate && <$DateLabel value={closedDate} />}
            </strong>
          </$Col>
        </$Row>

        <$Divider className="mb-4 mt-3" />

        <$Row>
          <$Col span={8} className="p-2">
            <h5 className="text-muted">
              {t("US.COLLECTION.COMMON:COMMON.ARCHIVED_DATE")}
            </h5>
            <strong>
              {archivedDate &&
              dayjs
                .utc(archivedDate)
                .isAfter(dayjs.utc(DateValues.minDate)) ? (
                <$DateLabel value={archivedDate} />
              ) : (
                "-"
              )}
            </strong>
          </$Col>
          <$Col span={16} className="p-2">
            <h5 className="text-muted">
              {t("US.COLLECTION.COMMON:COMMON.ARCHIVED_USER")}
            </h5>
            <strong>
              {!archivedUser && "-"}
              {archivedUser && archivedUser}
            </strong>
          </$Col>
        </$Row>
      </div>
    </$Skeleton>
  );
};

const mapStateToProps = (state: RootState) => {
  const { mainSearch } = state;
  const { archivedDetails } = mainSearch;

  return {
    archivedDetails,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DataArchive);
